import { useSpeakerManagementService } from '@src/hooks/useSpeakerManagementService';
import { useDatoPerson } from '@src/stores-v2/StoreContext';
import { fetchSignedUrls } from '@src/utility/story';
import { useCallback, useEffect, useRef, useState } from 'react';

import type {
  DatoSpeaker,
  SpeakerStoryRef,
} from '@src/repositories/SpeakerRepository';
import type { SpeakerData } from '@src/services/SpeakerManagementService';

/** Shape produced by `SpeakerFormModal`'s `onSubmit` callback. */
export type SpeakerFormData = SpeakerData;

/**
 * Fill in thumbnails for stories with none from DatoCMS (AWS/self-hosted
 * uploads whose thumbnail lives behind a signed CloudFront URL). Mirrors the
 * Dashboard's `fetchSignedUrls` enrichment so the Speaker Manager shows the
 * same story thumbnails the Story Manager does.
 */
async function enrichUsageWithSignedThumbnails(
  usage: Record<string, SpeakerStoryRef[]>,
): Promise<Record<string, SpeakerStoryRef[]>> {
  const missingIds = new Set<string>();
  for (const stories of Object.values(usage)) {
    for (const story of stories) {
      if (!story.thumbnailUrl) missingIds.add(story.id);
    }
  }
  if (missingIds.size === 0) return usage;

  const signed = await fetchSignedUrls([...missingIds]);
  if (Object.keys(signed).length === 0) return usage;

  const enriched: Record<string, SpeakerStoryRef[]> = {};
  for (const [speakerId, stories] of Object.entries(usage)) {
    enriched[speakerId] = stories.map(story => {
      const sig = signed[story.id];
      if (!sig || story.thumbnailUrl) return story;
      return {
        ...story,
        thumbnailUrl: sig.thumbnailUrl,
        durationSeconds: story.durationSeconds ?? sig.duration,
      };
    });
  }
  return enriched;
}

/** Map the form's `{ id, url }` headshots to the `DatoSpeaker` display shape. */
function toDisplayHeadshots(
  headshots: SpeakerFormData['headshots'],
): DatoSpeaker['headshots'] {
  return headshots?.map(h => ({ id: h.id, url: h.url })) ?? undefined;
}

/**
 * Loads and mutates the organization's speaker library for the Speaker Manager.
 *
 * All repository orchestration lives in `SpeakerManagementService`; this hook
 * holds the React state and maps the service's `Result` into the boolean/error
 * shape the UI consumes. Usage counts ("used in N stories") are derived
 * server-side from `story.speakers[]` — no backend endpoint required.
 */
export function useSpeakerManager(albumId: string | undefined) {
  const service = useSpeakerManagementService();
  const datoPerson = useDatoPerson();

  const [speakers, setSpeakers] = useState<DatoSpeaker[]>([]);
  const [usage, setUsage] = useState<Record<string, SpeakerStoryRef[]>>({});
  // Start loading so the initial fetch doesn't flash the empty-state message.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Tracks the album the most recent load belongs to, so a slow request (the
  // list fetch or the background thumbnail enrich) can't clobber state after
  // the user has switched organizations.
  const loadedAlbumRef = useRef<string | undefined>(undefined);

  const load = useCallback(async () => {
    if (!albumId) return;
    setLoading(true);
    setError(null);
    loadedAlbumRef.current = albumId;
    const result = await service.getOrgSpeakers(albumId);
    // A newer load (different album) started while this request was in flight;
    // discard the stale result so it can't overwrite the current album's data.
    if (loadedAlbumRef.current !== albumId) return;
    if (result.ok) {
      setSpeakers(result.value.speakers);
      // Render the list immediately with DatoCMS thumbnails; fill in the
      // signed-URL (AWS/self-hosted) thumbnails in the background so the
      // batch sign request never blocks the speaker list from painting.
      setUsage(result.value.usage);
      enrichUsageWithSignedThumbnails(result.value.usage).then(enriched => {
        if (
          enriched !== result.value.usage &&
          loadedAlbumRef.current === albumId
        ) {
          setUsage(enriched);
        }
      });
    } else {
      setError('Failed to load speakers. Please try again.');
    }
    setLoading(false);
  }, [albumId, service]);

  useEffect(() => {
    load();
  }, [load]);

  // Mutations apply their confirmed result to local state instead of calling
  // load(): the REST write has already succeeded when the service returns, but
  // the GraphQL list lags behind CDN propagation and would re-introduce the
  // just-removed (or omit the just-added) speaker.
  const createSpeaker = useCallback(
    async (data: SpeakerFormData): Promise<boolean> => {
      if (!albumId) return false;
      const result = await service.createOrgSpeaker(
        albumId,
        datoPerson.person?.id ?? null,
        data,
      );
      if (!result.ok) return false;
      const created: DatoSpeaker = {
        id: result.value.id,
        name: data.name,
        title: data.title,
        bio: data.bio,
        voice: data.voice,
        gender: data.gender || null,
        headshots: toDisplayHeadshots(data.headshots),
        createdByUser: datoPerson.person?.id
          ? { id: datoPerson.person.id, name: datoPerson.person.name }
          : null,
      };
      setSpeakers(prev => [...prev, created]);
      setUsage(prev => ({ ...prev, [created.id]: [] }));
      return true;
    },
    [albumId, service, datoPerson],
  );

  const updateSpeaker = useCallback(
    async (speakerId: string, data: SpeakerFormData): Promise<boolean> => {
      // Coerce blanks to '' so clearing a field in the editor clears it in
      // DatoCMS (the service's default treats `undefined` as "no change").
      const result = await service.updateSpeaker(speakerId, {
        name: data.name,
        title: data.title ?? '',
        bio: data.bio ?? '',
        voice: data.voice ?? '',
        gender: data.gender ?? '',
        headshots: data.headshots,
      });
      if (!result.ok) return false;
      setSpeakers(prev =>
        prev.map(s => {
          if (s.id !== speakerId) return s;
          const next: DatoSpeaker = {
            ...s,
            name: data.name,
            title: data.title ?? '',
            bio: data.bio ?? '',
            voice: data.voice ?? '',
            gender: data.gender || null,
          };
          // `undefined` = field omitted (no change); null/array = cleared/set.
          if (data.headshots !== undefined) {
            next.headshots = toDisplayHeadshots(data.headshots);
          }
          return next;
        }),
      );
      return true;
    },
    [service],
  );

  const removeSpeaker = useCallback(
    async (speakerId: string): Promise<boolean> => {
      if (!albumId) return false;
      const result = await service.removeOrgSpeaker(albumId, speakerId);
      if (!result.ok) return false;
      setSpeakers(prev => prev.filter(s => s.id !== speakerId));
      setUsage(prev => {
        const next = { ...prev };
        delete next[speakerId];
        return next;
      });
      return true;
    },
    [albumId, service],
  );

  return {
    speakers,
    usage,
    loading,
    error,
    reload: load,
    createSpeaker,
    updateSpeaker,
    removeSpeaker,
  };
}
